/**
 * Analytics utility for Plausible Analytics
 * Privacy-focused event tracking without PII
 */

/**
 * Check if Plausible is available
 * @returns {boolean}
 */
const isPlausibleAvailable = () => {
  return typeof window !== 'undefined' && typeof window.plausible !== 'undefined';
};

/**
 * Track a custom event with Plausible
 * @param {string} eventName - Name of the event
 * @param {object} props - Optional event properties (no PII!)
 */
export const trackEvent = (eventName, props = {}) => {
  if (!isPlausibleAvailable()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics Debug]', eventName, props);
    }
    return;
  }

  try {
    window.plausible(eventName, { props });
  } catch (error) {
    console.error('[Analytics Error]', error);
  }
};

/**
 * Timeline Analytics Events
 */
export const trackPlatformFilter = (platform) => {
  trackEvent('Platform Filter', { platform });
};

export const trackCategoryFilter = (category, action) => {
  trackEvent('Category Filter', { category, action }); // action: 'added' or 'removed'
};

export const trackCategoryFiltersCleared = (count) => {
  trackEvent('Category Filters Cleared', { count });
};

export const trackFeatureClicked = (featureName, platform, year) => {
  trackEvent('Feature Clicked', {
    feature: featureName,
    platform,
    year
  });
};

export const trackYearScrolled = (year) => {
  // Debounced tracking to avoid spam
  trackEvent('Year Viewed', { year });
};

/**
 * Toolkit Analytics Events
 */
export const trackToolClicked = (toolName, stage, tier) => {
  trackEvent('Tool Clicked', {
    tool: toolName,
    stage,
    tier
  });
};

export const trackStageViewed = (stageName) => {
  trackEvent('Stage Viewed', { stage: stageName });
};

/**
 * Hire Form Analytics Events
 */
export const trackHireFormViewed = () => {
  trackEvent('Hire Form Viewed');
};

export const trackHireFormStarted = () => {
  trackEvent('Hire Form Started');
};

export const trackHireFormSubmitted = (projectType) => {
  trackEvent('Hire Form Submitted', { projectType });
};

export const trackHireFormError = (errorType) => {
  trackEvent('Hire Form Error', { errorType });
};

/**
 * Navigation Analytics Events
 */
export const trackHeaderMenuOpened = () => {
  trackEvent('Header Menu Opened');
};

export const trackNavigation = (fromPage, toPage) => {
  trackEvent('Navigation', { from: fromPage, to: toPage });
};

export const trackThemeToggled = (theme) => {
  trackEvent('Theme Toggled', { theme });
};

export const trackHireMeButtonClicked = (location) => {
  trackEvent('Hire Me Button Clicked', { location }); // location: 'header' or 'page'
};

/**
 * Performance Analytics Events
 */
export const trackSlowFilterResponse = (filterType, duration) => {
  trackEvent('Slow Filter Response', {
    filterType,
    duration: Math.round(duration)
  });
};

export const trackErrorBoundary = (errorType, componentStack) => {
  trackEvent('Error Boundary Triggered', {
    errorType,
    component: componentStack
  });
};

export const trackLazyLoadTime = (componentName, duration) => {
  trackEvent('Lazy Load Time', {
    component: componentName,
    duration: Math.round(duration)
  });
};

export const trackPageLoadPerformance = (pageName, metrics) => {
  trackEvent('Page Load Performance', {
    page: pageName,
    loadTime: Math.round(metrics.loadTime),
    domReady: Math.round(metrics.domReady)
  });
};

/**
 * External Link Tracking
 */
export const trackExternalLink = (linkType, destination) => {
  trackEvent('External Link Clicked', {
    type: linkType,
    destination
  });
};

/**
 * Search Analytics (for future use)
 */
export const trackSearch = (query, resultsCount) => {
  trackEvent('Search Performed', {
    hasQuery: query.length > 0,
    resultsCount
  });
};

/**
 * Utility: Debounce function for scroll events
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Utility: Measure performance timing
 */
export const measurePerformance = (startTime) => {
  return performance.now() - startTime;
};
